/**
 * Main-thread handle on the engine worker.
 *
 * Owns the worker lifecycle and the subscription plumbing. Everything above this line in the app
 * deals in `Snapshot` and `Action`; nothing above it can reach `SimState`.
 */

import type { Action } from '../engine/actions';
import type { ScoreCard } from '../engine/scoring';
import type { SaveFile, Snapshot } from '../engine/snapshot';
import type { Difficulty } from '../engine/types';
import type { SandboxOptions } from '../scenarios/sandbox';
import type { FromWorker, SiteSlot, Speed, ToWorker } from './protocol';
import type { WhatIfRequest, WhatIfTrace } from './whatif';

export interface EngineHandlers {
  onSnapshot(s: Snapshot, secondary: Snapshot | null): void;
  onEnd(card: ScoreCard, s: Snapshot, secondaryCard: ScoreCard | null, secondary: Snapshot | null): void;
  onFault(message: string): void;
  onSaved(save: SaveFile): void;
  onWhatIf(id: number, traces: WhatIfTrace[]): void;
}

export class EngineClient {
  private worker: Worker;
  private handlers: Partial<EngineHandlers> = {};
  private whatIfSeq = 0;

  constructor() {
    this.worker = new Worker(new URL('./engine-worker.ts', import.meta.url), { type: 'module' });
    // Nothing fails silently (CLAUDE.md non-negotiable #6): a worker that throws on load or
    // during a step reports it the same way an EngineFault does.
    this.worker.onerror = (e: ErrorEvent): void => {
      this.handlers.onFault?.(`worker error: ${e.message} (${e.filename}:${e.lineno})`);
    };
    this.worker.onmessageerror = (): void => {
      this.handlers.onFault?.('worker message could not be deserialised');
    };
    this.worker.onmessage = (ev: MessageEvent<FromWorker>): void => {
      const m = ev.data;
      switch (m.type) {
        case 'snapshot':
          this.handlers.onSnapshot?.(m.snapshot, m.secondary);
          break;
        case 'scenarioEnd':
          this.handlers.onEnd?.(m.card, m.snapshot, m.secondaryCard, m.secondarySnapshot);
          break;
        case 'fault':
          this.handlers.onFault?.(m.message);
          break;
        case 'saved':
          this.handlers.onSaved?.(m.save);
          break;
        case 'whatIfResult':
          this.handlers.onWhatIf?.(m.id, m.traces);
          break;
        default:
          break;
      }
    };
  }

  on(h: Partial<EngineHandlers>): void {
    this.handlers = { ...this.handlers, ...h };
  }

  private send(msg: ToWorker): void {
    this.worker.postMessage(msg);
  }

  init(
    siteId: string,
    scenarioId: string,
    difficulty: Difficulty,
    seed: number,
    sandbox: SandboxOptions | null = null,
  ): void {
    this.send({ type: 'init', siteId, scenarioId, difficulty, seed, sandbox });
  }

  load(save: SaveFile): void {
    this.send({ type: 'load', save });
  }

  dispatch(site: SiteSlot, ...actions: Action[]): void {
    if (actions.length > 0) this.send({ type: 'dispatch', actions, site });
  }

  setSpeed(speed: Speed): void {
    this.send({ type: 'setSpeed', speed });
  }

  pause(): void {
    this.send({ type: 'pause' });
  }

  resume(): void {
    this.send({ type: 'resume' });
  }

  requestSave(name: string): void {
    this.send({ type: 'requestSave', name });
  }

  requestSnapshot(): void {
    this.send({ type: 'requestSnapshot' });
  }

  whatIf(requests: WhatIfRequest[]): number {
    const id = ++this.whatIfSeq;
    this.send({ type: 'whatIf', id, requests });
    return id;
  }

  cancelWhatIf(): void {
    this.send({ type: 'cancelWhatIf' });
  }

  terminate(): void {
    this.worker.terminate();
  }
}
